import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay, Pagination } from 'swiper';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { useRef, useCallback } from 'react';
import Image from 'next/image';

import { BrandBannerModel } from '../../../interface/products';
import { useMediaQuery, MediaQuery } from '../../../hooks/useMediaQuery';
import { brandColors } from '../../../constants/colors';
import * as S from './styles';

/* eslint-disable-next-line */
type BrandBannerCarouselProps = {
  brandBannerContent: BrandBannerModel[];
  paginationColor: typeof brandColors.SUBZERO_HEX | typeof brandColors.WOLF_HEX;
  textColor: string;
};

export function BrandBannerCarousel(props: BrandBannerCarouselProps) {
  const { brandBannerContent, paginationColor, textColor } = props;
  const isTablet = useMediaQuery(MediaQuery.MD);
  const sliderRef = useRef() as any;

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const navigation = (
    <div className="hidden md:block">
      <BsChevronLeft
        className={`navigation absolute left-10 top-1/2 -translate-y-1/2 text-header4 ${textColor} z-20 cursor-pointer`}
        onClick={handlePrev}
      />
      <BsChevronRight
        className={`navigation absolute right-10 top-1/2 -translate-y-1/2 text-header4 ${textColor} z-20 cursor-pointer`}
        onClick={handleNext}
      />
    </div>
  );

  const slides = brandBannerContent.map((carouselItem) => {
    const { bannerImage, bannerDescription, bannerTitle } = carouselItem;

    return (
      <SwiperSlide
        className="flex flex-col h-full items-center pb-12 px-2"
        key={bannerTitle}
      >
        <Image
          alt="carousel image"
          blurDataURL="/icons/blur-placeholder.png"
          height={500}
          objectFit="cover"
          placeholder="blur"
          src={bannerImage?.data?.attributes?.url}
          width={bannerImage?.data?.attributes?.width}
        />

        <div className="text-center px-12 lg:px-36 mt-4">
          <p
            className={`text-3header6 lg:text-header5 mb-3 font-MuseoSans100 uppercase ${textColor}`}
          >
            {bannerTitle}
          </p>
          <p className="text-caption lg:text-subtitle leading-6 font-MuseoSans100">
            {bannerDescription}
          </p>
        </div>
      </SwiperSlide>
    );
  });

  return (
    <S.SwiperContainer
      paginationColor={paginationColor}
      shouldDisplayNavigation={!isTablet}
      shouldDisplayPagination={isTablet}
    >
      {navigation}
      <Swiper
        autoplay={{
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }}
        loop={true}
        modules={[Autoplay, Pagination]}
        onInit={(core: SwiperCore) => {
          sliderRef.current = core.el;
        }}
        pagination={{
          clickable: true
        }}
        style={{
          position: 'unset'
        }}
        speed={900}
      >
        {slides}
      </Swiper>
    </S.SwiperContainer>
  );
}

export default BrandBannerCarousel;
